import * as ko from "knockout";
import {global} from "../../../appContext";
import pubsub from "../../../pubsub";
import {ICloneDialogOptions} from "../../../clone/index";
import {IRuleset, ISearchParams} from "../../../index";
import {MAX_RESULTS_PER_PAGE} from "../../../constants";
import {REQUEST_MESSAGE, UPDATING_MESSAGE} from "../../../constants/Language";
import {getHash} from "../../../constants/RouteDefinitions";
import {createErrorMessage} from "../../../util/misc";
import {nsRuleset} from "../nsRuleset";
import {nsClone} from "../../../clone/fn";
import targetOrg = nsClone.targetOrg;
import {
    collectUniqueRulesetsFromRoots,
    copyRulesetsWithApis,
    ICopyRulesetResult,
    topologicalCopyOrder,
} from "./copyRulesetGraph";
import type {IStepperStep} from "./sub/stepper-host";
import {RulesetNameConflictRowVm} from "./rulesetNameConflictRow";
import mainHtml from './main.html?raw';

export const template = mainHtml;

const PREFIX = "ruleset.copy.wizard";

export enum WizardInternalStep {
    LoadSource = 1,
    PickTargetOrg = 2,
    LoadTarget = 3,
    ResolveConflicts = 4,
    Process = 5,
    Receipt = 6,
}

export interface ICopyWizardParams {
    dialog: any;
    item?: IRuleset;
    selectedObjects?: IRuleset[];
    options?: ICloneDialogOptions;
    requestParams?: ISearchParams;
}

export class viewModel {
    readonly internalStep = ko.observable<WizardInternalStep>(WizardInternalStep.LoadSource);
    readonly messageData = ko.observable(null);
    readonly headingKey = ko.observable(`${PREFIX}.loadsource`);
    readonly descriptionKey = ko.observable(`${PREFIX}.loadsource.description`);
    readonly showStepper = ko.observable(false);
    readonly stepperCurrentIndex = ko.observable(0);
    readonly stepperSteps: ReadonlyArray<IStepperStep> = [
        {
            labelKey: `${PREFIX}.stepper.picktargetorg`,
            descriptionKey: `${PREFIX}.stepper.picktargetorg.description`,
        },
        {
            labelKey: `${PREFIX}.stepper.resolveconflicts`,
            descriptionKey: `${PREFIX}.stepper.resolveconflicts.description`,
        },
        {
            labelKey: `${PREFIX}.stepper.receipt`,
            descriptionKey: `${PREFIX}.stepper.receipt.description`,
        },
    ];

    readonly srcOrg = global.login.selectedOrganization;
    readonly organizations = global.login.organizations;
    readonly trgOrg = targetOrg;

    readonly uniqueRulesets = ko.observableArray<IRuleset>([]);
    readonly conflictRows = ko.observableArray<RulesetNameConflictRowVm>([]);
    readonly copyResults = ko.observableArray<ICopyRulesetResult>([]);

    readonly showPrevious = ko.observable(false);
    readonly showNext = ko.observable(false);
    readonly showCancel = ko.observable(true);
    readonly canGoPrevious = ko.pureComputed(
        () => this.internalStep() === WizardInternalStep.ResolveConflicts,
    );
    readonly canGoNext = ko.pureComputed((): boolean => {
        switch (this.internalStep()) {
            case WizardInternalStep.PickTargetOrg:
                return !!this.trgOrg()?.organizationid;
            case WizardInternalStep.ResolveConflicts:
                return this.canAdvanceFromConflictStep();
            default:
                return false;
        }
    });
    readonly nextLabelKey = ko.observable("next");
    readonly nextButtonClassMap = ko.observable<Record<string, boolean>>({
        "btn-primary": true,
        "btn-success": false,
    });
    readonly cancelLabelKey = ko.observable("cancel");
    readonly cancelIcon = ko.observable("fa-ban");

    private sourceRulesetsById = new Map<number, IRuleset>();
    private targetRulesetsList: IRuleset[] = [];
    private activeSearchRequest: ReturnType<typeof global.getPromiseRequest> | null = null;

    constructor(private readonly params: ICopyWizardParams) {
        const src = this.srcOrg();
        if (!this.trgOrg() && this.organizations().length && src) {
            this.trgOrg(src);
        }
        this.internalStep.subscribe((s) => this.onStepChanged(s));
        this.onStepChanged(this.internalStep());
    }

    readonly canAdvanceFromConflictStep = ko.pureComputed((): boolean => {
        if (this.internalStep() !== WizardInternalStep.ResolveConflicts) {
            return false;
        }
        this.conflictRows().forEach((r) => {
            r.draftName();
        });
        return this.conflictRows().every((r) => !r.hasBlockingConflict());
    });

    private onStepChanged(step: WizardInternalStep) {
        this.updateChrome(step);
        switch (step) {
            case WizardInternalStep.LoadSource:
                this.runLoadSource();
                break;
            case WizardInternalStep.PickTargetOrg:
                break;
            case WizardInternalStep.LoadTarget:
                this.runLoadTarget();
                break;
            case WizardInternalStep.ResolveConflicts:
                this.prepareConflictRows();
                break;
            case WizardInternalStep.Process:
                this.runProcess();
                break;
            case WizardInternalStep.Receipt:
                break;
            default:
                break;
        }
    }

    private updateChrome(step: WizardInternalStep) {
        const keyOf = (k: string) => `${PREFIX}.${k}`;
        const showPnNav = step !== WizardInternalStep.Receipt;
        this.showPrevious(showPnNav);
        this.showNext(showPnNav);
        switch (step) {
            case WizardInternalStep.LoadSource:
                this.headingKey(keyOf("loadsource"));
                this.descriptionKey(keyOf("loadsource.description"));
                this.showStepper(false);
                this.showCancel(true);
                this.nextButtonClassMap({"btn-primary": true, "btn-success": false});
                this.cancelLabelKey("cancel");
                this.cancelIcon("fa-ban");
                break;
            case WizardInternalStep.PickTargetOrg:
                this.headingKey(keyOf("itemslist"));
                this.descriptionKey(keyOf("itemslist.description"));
                this.showStepper(true);
                this.stepperCurrentIndex(0);
                this.showCancel(true);
                this.nextLabelKey("next");
                this.nextButtonClassMap({"btn-primary": true, "btn-success": false});
                this.cancelLabelKey("cancel");
                this.cancelIcon("fa-ban");
                break;
            case WizardInternalStep.LoadTarget:
                this.headingKey(keyOf("loadtarget"));
                this.descriptionKey(keyOf("loadtarget.description"));
                this.showStepper(false);
                this.showCancel(true);
                this.nextButtonClassMap({"btn-primary": true, "btn-success": false});
                this.cancelLabelKey("cancel");
                this.cancelIcon("fa-ban");
                break;
            case WizardInternalStep.ResolveConflicts:
                this.headingKey(keyOf("resolveconflicts"));
                this.descriptionKey(keyOf("resolveconflicts.description"));
                this.showStepper(true);
                this.stepperCurrentIndex(1);
                this.showCancel(true);
                this.nextLabelKey(keyOf("submit"));
                this.nextButtonClassMap({"btn-primary": false, "btn-success": true});
                this.cancelLabelKey("cancel");
                this.cancelIcon("fa-ban");
                break;
            case WizardInternalStep.Process:
                this.headingKey(keyOf("process"));
                this.descriptionKey(keyOf("process.description"));
                this.showStepper(false);
                this.showCancel(true);
                this.nextButtonClassMap({"btn-primary": true, "btn-success": false});
                this.cancelLabelKey("cancel");
                this.cancelIcon("fa-ban");
                break;
            case WizardInternalStep.Receipt:
                this.headingKey(keyOf("receipt"));
                this.descriptionKey(keyOf("receipt.description"));
                this.showStepper(true);
                this.stepperCurrentIndex(2);
                this.showCancel(true);
                this.cancelLabelKey("close");
                this.cancelIcon("fa-times");
                break;
            default:
                break;
        }
    }

    private getRootRulesets(): IRuleset[] {
        if (this.params.selectedObjects?.length) {
            return this.params.selectedObjects;
        }
        if (this.params.item) {
            return [this.params.item];
        }
        return [];
    }

    private runLoadSource() {
        this.setDialogLocked(false);
        this.messageData({message: REQUEST_MESSAGE});
        const srcOrg = this.srcOrg();
        if (!srcOrg) {
            this.messageData(createErrorMessage("missing organization"));
            return;
        }
        const orgnr = srcOrg.organizationid;
        const req = global.getPromiseRequest("searchruleset");
        this.activeSearchRequest = req;
        req.request({
            searchkey: "",
            resultsprpage: MAX_RESULTS_PER_PAGE,
            pagenr: 0,
            orgnr,
            sortcol: "name",
            asc: true,
        })
            .then((data: IRuleset[]) => {
                this.sourceRulesetsById = new Map(data.map((r) => [r.id, r]));
                const roots = this.getRootRulesets()
                    .map((r) => this.sourceRulesetsById.get(r.id) || r)
                    .filter(Boolean) as IRuleset[];
                const unique = collectUniqueRulesetsFromRoots(roots, this.sourceRulesetsById);
                this.uniqueRulesets(unique);
                this.messageData(null);
                this.internalStep(WizardInternalStep.PickTargetOrg);
            })
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)));
            });
    }

    private runLoadTarget() {
        this.messageData({message: REQUEST_MESSAGE});
        const orgnr = this.trgOrg()?.organizationid;
        if (!orgnr) {
            this.messageData(createErrorMessage("missing organization"));
            return;
        }
        const req = global.getPromiseRequest("searchruleset");
        this.activeSearchRequest = req;
        req.request({
            searchkey: "",
            resultsprpage: MAX_RESULTS_PER_PAGE,
            pagenr: 0,
            orgnr,
            sortcol: "name",
            asc: true,
        })
            .then((data: IRuleset[]) => {
                this.targetRulesetsList = data;
                this.messageData(null);
                this.internalStep(WizardInternalStep.ResolveConflicts);
            })
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)));
            });
    }

    private prepareConflictRows() {
        const unique = this.uniqueRulesets();
        const nameCounts = new Map<string, number>();
        for (const r of unique) {
            const key = r.name.trim();
            nameCounts.set(key, (nameCounts.get(key) ?? 0) + 1);
        }
        const built: RulesetNameConflictRowVm[] = [];
        unique.forEach((rs) => {
            const batchDup = (nameCounts.get(rs.name.trim()) ?? 0) > 1;
            built.push(
                new RulesetNameConflictRowVm(
                    rs,
                    () => this.targetRulesetsList,
                    () =>
                        built
                            .filter((r) => r.ruleset.id !== rs.id)
                            .map((r) => r.draftName().trim()),
                    batchDup,
                ),
            );
        });
        this.conflictRows(built);
    }

    private runProcess() {
        this.setDialogLocked(true);
        this.messageData({message: UPDATING_MESSAGE});
        const trgOrgnr = this.trgOrg().organizationid;
        const nameMap = new Map<number, string>();
        this.conflictRows().forEach((row) => {
            nameMap.set(row.ruleset.id, row.draftName().trim());
        });
        const ordered = topologicalCopyOrder(this.uniqueRulesets());
        copyRulesetsWithApis(ordered, trgOrgnr, nameMap)
            .then((results) => {
                this.copyResults(results);
                nsRuleset.invalidateCachesAfterRulesetMutation();
                if (results.some((r) => r.success)) {
                    pubsub.trigger("ruleset.copy.success");
                }
                this.messageData(null);
                this.setDialogLocked(false);
                this.internalStep(WizardInternalStep.Receipt);
            })
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)));
                this.setDialogLocked(false);
            });
    }

    private setDialogLocked(locked: boolean) {
        const d = this.params.dialog;
        if (d?.closeable && ko.isObservable(d.closeable)) {
            d.closeable(!locked);
        }
        if (d?.isClosable && ko.isObservable(d.isClosable)) {
            d.isClosable(!locked);
        }
    }

    rulesetHref = (rs: IRuleset): string => getHash("rulesetdetails", {id: rs.id, orgnr: rs.companyid}) ?? '';

    onPreviousClick = () => {
        if (this.internalStep() === WizardInternalStep.ResolveConflicts) {
            this.internalStep(WizardInternalStep.PickTargetOrg);
        }
    };

    onNextClick = () => {
        switch (this.internalStep()) {
            case WizardInternalStep.PickTargetOrg:
                this.internalStep(WizardInternalStep.LoadTarget);
                break;
            case WizardInternalStep.ResolveConflicts:
                this.internalStep(WizardInternalStep.Process);
                break;
            default:
                break;
        }
    };

    onCancelClick = () => {
        this.closeDialog();
    };

    private closeDialog() {
        const d = this.params.dialog;
        if (typeof d?.doClose === "function") {
            d.doClose();
        } else {
            pubsub.trigger("close.dialog", d);
        }
    }

    resultForSourceId(id: number): ICopyRulesetResult | undefined {
        return this.copyResults().find((r) => r.sourceId === id);
    }

    readonly receiptRows = ko.pureComputed(() => {
        if (!this.copyResults().length) {
            return [];
        }
        return this.uniqueRulesets()
            .map((ruleset) => ({
                ruleset,
                result: this.resultForSourceId(ruleset.id),
            }))
            .filter((row): row is {ruleset: IRuleset; result: ICopyRulesetResult} => !!row.result);
    });

    readonly receiptErrorCount = ko.pureComputed(
        () => this.copyResults().filter((r) => !r.success).length,
    );

    readonly receiptHasErrors = ko.pureComputed(() => this.receiptErrorCount() > 0);

    receiptBannerKey = ko.pureComputed(() =>
        this.receiptHasErrors()
            ? `${PREFIX}.receipt.errorsoccured|n:${this.receiptErrorCount()}`
            : null,
    );

    dispose() {
        this.activeSearchRequest?.abort();
    }
}
